import React from "react";
import { Seo } from "@/components/Seo";

const CONTENT = {
  privacy: {
    title: "Privacy Policy",
    body: (
      <>
        <p>The PM Test ("we", "us") respects your privacy. This policy explains what we collect and how we use it.</p>
        <h2>Information we collect</h2>
        <p>We collect information you voluntarily provide — for example, when you submit the "Share Your Stack" form. We also collect standard, anonymized analytics data (such as pages visited and approximate visitor counts) to understand how the site is used.</p>
        <h2>Affiliate & click tracking</h2>
        <p>When you click an affiliate link, we record the click along with an anonymous session identifier. We do not collect names, emails, or personal data through this mechanism.</p>
        <h2>Reader submissions</h2>
        <p>Feedback submitted through our forms is stored privately and is never published or quoted unless you explicitly grant permission — and even then, always anonymized.</p>
        <h2>Third-party services</h2>
        <p>We may use third-party analytics (such as Google Analytics) and search tools (such as Google Search Console). These services may set their own cookies subject to their privacy policies.</p>
        <h2>Contact</h2>
        <p>Questions about this policy can be directed to the site administrator.</p>
      </>
    ),
  },
  terms: {
    title: "Terms of Use",
    body: (
      <>
        <p>By using thepmtest.com you agree to these terms.</p>
        <h2>Editorial content</h2>
        <p>Our reviews and scores reflect our independent editorial opinion based on hands-on evaluation. They are provided for informational purposes and do not constitute professional, legal, or financial advice.</p>
        <h2>Accuracy</h2>
        <p>Product features, pricing, and availability change frequently. We strive for accuracy but make no warranties. Always verify details with the vendor before purchasing.</p>
        <h2>Affiliate links</h2>
        <p>Some links are affiliate links. See our Affiliate Disclosure for details.</p>
        <h2>Limitation of liability</h2>
        <p>The PM Test is not liable for any decisions made based on our content. Use of this site is at your own risk.</p>
      </>
    ),
  },
  affiliate: {
    title: "Affiliate Disclosure",
    body: (
      <>
        <div className="border-l-4 border-stone-900 bg-white p-6 my-6 not-prose">
          <p className="font-mono-pm text-sm text-stone-800 leading-relaxed">The PM Test may earn a commission when you purchase or sign up through certain links, at no additional cost to you. Affiliate relationships do not influence our scores or recommendations.</p>
        </div>
        <h2>How it works</h2>
        <p>Some of the links on this site are affiliate links. If you click one and make a purchase or sign up for a service, we may receive a commission from the vendor. This comes at no additional cost to you.</p>
        <h2>Our commitment</h2>
        <p>Affiliate commissions never influence our PM Test Scores, category ratings, verdicts, or recommendations. Our scoring rubric is applied identically to every product, whether or not an affiliate relationship exists. A product that fails The PM Test will be reported as failing, regardless of commercial relationships.</p>
      </>
    ),
  },
  editorial: {
    title: "Editorial Policy",
    body: (
      <>
        <p>The PM Test exists to give property managers honest, operator-led assessments of the technology they rely on.</p>
        <h2>How we score</h2>
        <p>Every product is evaluated across nine standardized categories, each rated 0–10, producing a PM Test Score out of 100. A product must reach our published threshold to earn a "Passes The PM Test" verdict.</p>
        <h2>No fabricated results</h2>
        <p>We never publish a PM Test Score, category rating, or verdict until a genuine, hands-on editorial review is complete. Draft and in-progress reviews are never publicly displayed with scores.</p>
        <h2>Independence</h2>
        <p>Our editorial judgment is independent of any commercial or affiliate relationship. Vendors do not review or approve our content before publication.</p>
        <h2>Corrections & updates</h2>
        <p>Products evolve. We revisit and update reviews as products change, and every review carries a "last reviewed" date.</p>
      </>
    ),
  },
};

export default function Legal({ page }) {
  const c = CONTENT[page];
  return (
    <div>
      <Seo title={c.title} />
      <section className="border-b-2 border-stone-900">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 py-14">
          <p className="font-mono-pm text-xs tracking-[0.25em] uppercase text-[#991B1B] mb-4">Legal</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl font-black text-stone-900">{c.title}</h1>
        </div>
      </section>
      <section className="max-w-3xl mx-auto px-4 sm:px-8 py-12 pm-prose">
        {c.body}
        <p className="font-mono-pm text-xs text-stone-400 mt-10">Last updated {new Date().toISOString().slice(0, 10)}</p>
      </section>
    </div>
  );
}
